import { Box, Divider, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { GISTMRequisitos } from "../../services/api/gistm";
import { HidroDXEditor } from "../edit";
import { Close, Edit } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { RequestError, useApi } from "../../services";
import { ErrorOnDialog, RemoveButton } from "../modals";
import { objectToHash } from "../../core";
import { GISTMRequisitoModal } from "./requisito_edit_modal";

export interface GISTMRequisitoItemProps {
  editable?: boolean;
  onEdit?: (item: GISTMRequisitos) => void;
  onRemove?: (item: GISTMRequisitos) => void;
  initRequisito: GISTMRequisitos;
  bgColor?: string;
}

export const GISTMRequisitoItem: React.FC<GISTMRequisitoItemProps> = ({ onEdit, onRemove, initRequisito, editable, bgColor }) => {
  const api = useApi();

  const [error, setError] = useState<RequestError>();
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editObs, setEditObs] = useState(false);
  const [requisito, setRequisito] = useState<GISTMRequisitos>(initRequisito);
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    if (starting) {
      setStarting(false);
      return;
    }

    api.gistm.updateRequisito(requisito).then(val => {
      if ("error" in val) {
        setError(val);
        return;
      }

      if (objectToHash({ ...val, dataModificacao: undefined }) !== objectToHash({ ...requisito, dataModificacao: undefined })) {
        setRequisito(val);
      }
    });

  }, [requisito]);

  const onDelete = () => {
    api.gistm.removeRequisito(requisito.id).then(val => {
      if ("error" in val) {
        setError(val);
        return;
      }
      onRemove?.(val);
    });
  };

  const onEditObs = (obs: string) => {
    setRequisito(prev => ({ ...prev, obsCliente: obs }));
  };


  return <>
    {error && <ErrorOnDialog
      open={Boolean(error)}
      error={error.error}
      message={error.message}
      onClose={() => setError(undefined)}
    />}

    {openEditModal && <GISTMRequisitoModal
      initRequisito={requisito}
      open={Boolean(openEditModal)}
      barragem={requisito.idBarragem}
      onClose={() => setOpenEditModal(false)}
      onCancel={() => setOpenEditModal(false)}
      onFinish={() => {
        onEdit?.(requisito);
        setOpenEditModal(false);
      }}
    />}

    <Paper elevation={2} sx={{
      py: "5px",
      px: "15px",
      gap: "5px",
      width: "100%",
      display: "flex",
      alignItems: "start",
      flexDirection: "column",
      bgcolor: bgColor,
    }}>

      {/* Principio e Requisito */}
      <Box sx={{
        flex: 1,
        gap: "15px",
        width: "100%",
        display: "flex",
        flexDirection: 'row',
        alignItems: "center",
      }}>
        <Typography fontWeight={"bold"} fontSize={32}>{requisito.principio}.{requisito.requisito}</Typography>
        <Divider flexItem orientation="vertical" />

        <Typography fontWeight={"bold"}>Peso:</Typography>
        <Typography >{requisito.peso.toSignificantDigits("percent")}</Typography>

        <div style={{ display: "flex", flex: 1 }} />

        {editable && <Tooltip title="Editar Requisito">
          <IconButton onClick={() => setOpenEditModal(true)}>
            <Edit />
          </IconButton>
        </Tooltip>}

        {editable && <RemoveButton
          title="Remover Requisito"
          message="Esta ação é irreversível!"
          onRemove={onDelete}
        />}
      </Box>

      <Divider flexItem />

      {/* Aplicação */}
      <Box sx={{
        flex: 1,
        gap: "5px",
        width: "100%",
        display: "flex",
        flexDirection: 'row',
        alignItems: "center",
      }}>
        <Typography sx={{ flex: 1 }} fontWeight={"bold"} fontSize={12}>APLICAÇÃO DO REQUISITO:</Typography>
        <Typography sx={{ flex: 1 }} fontWeight={"bold"} fontSize={12} color={requisito.aplicacao ? "success" : "error"}>{requisito.aplicacao ? "APLICÁVEL" : "NÃO APLICÁVEL"}</Typography>
      </Box>

      {/* Documentação */}
      <Box sx={{
        flex: 1,
        gap: "5px",
        width: "100%",
        display: "flex",
        flexDirection: 'row',
        alignItems: "center",
      }}>
        <Typography sx={{ flex: 1 }} fontWeight={"bold"} fontSize={12}>DOCUMENTAÇÃO DISPONIBILIZADA ATENDE AO REQUISITO?</Typography>
        <Typography sx={{ flex: 1 }} fontWeight={"bold"} fontSize={12} color={documentacaoMap(requisito.documentacao)[1]}>{documentacaoMap(requisito.documentacao)[0]}</Typography>
      </Box>

      <Divider flexItem />

      {/* Descrição */}
      <div style={{ textAlign: "justify", height: "95px", overflowY: "auto", fontSize: 12 }}>
        <HidroDXEditor readOnly startText={requisito.desc} />
      </div>

      <Divider flexItem />

      {/* OBS HBR */}
      <Typography fontWeight={"bold"} fontSize={12}>Observação HIDROBR:</Typography>
      <div style={{ textAlign: "justify", height: "95px", overflowY: "auto", fontSize: 12 }}>
        <HidroDXEditor readOnly startText={requisito.obsHbr ?? ""} />
      </div>

      <Divider flexItem />

      {/* OBS Cliente */}
      <Box sx={{
        gap: "15px",
        width: "100%",
        display: "flex",
        flexDirection: 'row',
        alignItems: "center",
      }}>
        <Typography fontWeight={"bold"} fontSize={12}>Observação Cliente:</Typography>

        <div style={{ display: "flex", flex: 1 }} />

        <Tooltip title="Editar Observações">
          <IconButton onClick={() => setEditObs(prev => !prev)} size="small">
            {editObs ? <Close fontSize="small" /> : <Edit fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      <Paper elevation={0} sx={{
        fontSize: 12,
        width: "100%",
        height: "115px",
        overflow: "auto",
        overflowX: "auto",
        textAlign: "justify",
        bgcolor: editObs ? undefined : "transparent",
      }}>
        <HidroDXEditor
          readOnly={!editObs}
          startText={requisito.obsCliente ?? ""}
          onChange={onEditObs}
        />
      </Paper>

    </Paper>
  </>;
};

const documentacaoMap = (doc: "sim" | "parcial" | "inexistente" | "não necessário") => {
  switch (doc) {
    case "sim":
      return ["ATENDE", "sucess"];
    case "inexistente":
      return ["NÃO EXISTENTE", "error"];
    case "parcial":
      return ["ATENDE PARCIALMENTE", "info"];
    case "não necessário":
      return ["NÃO HÁ NECESSIDADE DE DOCUMENTAÇÃO", undefined];
    default:
      return ["NÃO HÁ NECESSIDADE DE DOCUMENTAÇÃO", undefined];
  }
};